import { useModalContentToggler } from '@components'

import { Button, ButtonsGroup, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { CreatePrepaymentResponseData } from '@views/bills/query-api'
import { getPaymentRegistrationModalView } from '@views/bills/utils/getPaymentRegistrationModalView'

import { useSegment } from '../../../../hooks'
import { Bill } from '../../../bills/types/bill'
import {
  useContactBalance,
  useMultiplePayableSubjects,
  usePaymentModal,
} from '../../../paymentReconciliationModal/contexts'
import { useSubjectReconciliationSuggestions } from '../../../paymentReconciliationModal/hooks'
import { PaymentRegistrationSuccessState } from '../../enums/paymentRegistrationSuccessState'
import { useRegisterPrepayment } from '../../hooks/useRegisterPrepayment'
import { getSubjectBalanceSum } from '../../utils/getSubjectBalanceSum'
import { notifyPaymentRegistrationSuccess } from '../../utils/notifyPaymentRegistrationSuccess'

export const PrepaymentModalFooter = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { paymentDate, modalId } = usePaymentModal()
  const { initialSubject, isCreditNoteSelected } = useMultiplePayableSubjects<Bill>()
  const { setActiveState } = useModalContentToggler()
  const { subjectHasMatchSuggestion } = useSubjectReconciliationSuggestions(initialSubject)
  const { contact, contactBalance } = useContactBalance()
  const { close: closeReconciliationModal } = useModal(modalId)

  const { registerContactBalancePayment, isProcessing } = useRegisterPrepayment({
    contact,
    contactBalance,
    subject: initialSubject,
    paymentDate,
    onSuccess: (data: CreatePrepaymentResponseData): void => {
      const billBalanceSum = getSubjectBalanceSum(data?.bills || [])
      if (billBalanceSum === 0) {
        notifyPaymentRegistrationSuccess(PaymentRegistrationSuccessState.RegisteredWithPrepayment, t)
        closeReconciliationModal()
        return
      }
      setActiveState(getPaymentRegistrationModalView(false, subjectHasMatchSuggestion, isCreditNoteSelected))
    },
  })

  const handleUsePrepaymentButtonClick = useCallback(() => {
    registerContactBalancePayment()
  }, [registerContactBalancePayment])

  const handleUseWithoutPrepaymentButtonClick = useCallback(() => {
    const nextModalViewId = getPaymentRegistrationModalView(false, subjectHasMatchSuggestion, isCreditNoteSelected)
    setActiveState(nextModalViewId)
    track('xxx - expenses - Prepayment modal - pay without prepayment clicked')
  }, [subjectHasMatchSuggestion, setActiveState, track, isCreditNoteSelected])

  return (
    <ButtonsGroup>
      <Button disabled={isProcessing} onClick={handleUseWithoutPrepaymentButtonClick} variant="outlined">
        {t('bill.payment_reconcile.button.pay_without_prepayment')}
      </Button>
      <Button disabled={isProcessing || !paymentDate} onClick={handleUsePrepaymentButtonClick}>
        {t('bill.payment_reconcile.button.use_prepayment')}
      </Button>
    </ButtonsGroup>
  )
}
